import { isDefined } from "@clipboard-health/util-ts";
import { type DateRange } from "@src/appV2/lib";

import { formatTimeRange } from "./formatTimeRange";
import { formatTimezoneWithOffset } from "./formatTimezoneWithOffset";
import { getTimezoneAbbreviation } from "./getTimezoneAbbreviation";
import { getTimezoneDifferenceInHours } from "./getTimezoneDifferenceInHours";

interface GetFormattedTimeRangeWithTimezoneParams {
  dateRange: DateRange;
  deviceTimezone?: string;
  timezone?: string;
  useNowForStartDate?: boolean;
}

/**
 * This function returns text with a time range in the format "9:30 am - 2:15 pm"
 * If `timezone` and `deviceTimezone` are passed and different, it will also return:
 * - The abbreviation of the reference timezone
 * - The formatted timezone with offset information
 *
 * Example:
 * - { label: "9:30 am - 2:15 pm", timezoneAbbreviation: undefined, timezoneWithOffset: undefined }
 * - { label: "9:30 am - 2:15 pm (EST)", timezoneAbbreviation: "EST", timezoneWithOffset: "EST (3 hrs later)" }
 */
export function getFormattedTimeRangeWithTimezone({
  dateRange,
  deviceTimezone,
  timezone,
  useNowForStartDate = false,
}: GetFormattedTimeRangeWithTimezoneParams) {
  const label = formatTimeRange(dateRange, { timezone, useNowForStartDate });

  const showTimezone =
    isDefined(timezone) && isDefined(deviceTimezone) && deviceTimezone !== timezone;

  const timezoneAbbreviation = showTimezone ? getTimezoneAbbreviation(timezone) : undefined;
  const timezoneWithOffset = showTimezone
    ? formatTimezoneWithOffset(timezone, getTimezoneDifferenceInHours(timezone, deviceTimezone))
    : undefined;

  return {
    label,
    timezoneAbbreviation,
    timezoneWithOffset,
  };
}
